<script lang="ts">
import SuperAdminLayout from "@/layouts/SuperAdminLayout.vue"

export default { layout: SuperAdminLayout }
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3"
import { SbList, SbListItem, SbListLabel, SbListSection } from "@unio/saltbox-ui"
import { ref } from "vue"

import DeleteConfirmModal from "@/components/DeleteConfirmModal.vue"
import DetailRow from "@/components/DetailRow.vue"
import FlashAlerts from "@/components/FlashAlerts.vue"
import LabeledSection from "@/components/LabeledSection.vue"
import RoleBadge from "@/components/RoleBadge.vue"
import { formatDate } from "@/lib/format"
import { ICON } from "@/lib/icons"
import {
  editSuperAdminUserPath,
  superAdminAccountPath,
  superAdminUserPath,
  toggleActiveSuperAdminUserPath,
} from "@/routes"
import type { UserAccountMembership } from "@/types/models"

const props = defineProps<{
  user: {
    id: string
    first_name: string
    last_name: string
    full_name: string
    email_address: string
    initials: string
    avatar_url: string | null
    super_admin: boolean
    active: boolean
    created_at: string
    accounts: UserAccountMembership[]
  }
  is_current_user: boolean
}>()

const deleteOpen = ref(false)
const deleting = ref(false)
const toggling = ref(false)

function toggleActive() {
  toggling.value = true
  router.patch(
    toggleActiveSuperAdminUserPath(props.user.id),
    {},
    { onFinish: () => (toggling.value = false) },
  )
}

function confirmDelete() {
  deleting.value = true
  router.delete(superAdminUserPath(props.user.id), {
    onFinish: () => {
      deleting.value = false
      deleteOpen.value = false
    },
  })
}
</script>

<template>
  <Head :title="`${user.full_name} — Admin`" />

  <div class="flex flex-col gap-6">
    <FlashAlerts />

    <div class="flex items-center gap-4">
      <UAvatar
        :src="user.avatar_url ?? undefined"
        :text="user.initials"
        :alt="user.first_name"
        size="lg"
      />
      <div>
        <h2 class="text-xl font-bold">{{ user.full_name }}</h2>
        <p class="text-muted text-sm">{{ user.email_address }}</p>
      </div>
    </div>

    <SbList bordered separator>
      <DetailRow label="Role">
        <UBadge v-if="user.super_admin" color="primary" variant="subtle">Super Admin</UBadge>
        <span v-else class="text-muted text-sm">Member</span>
      </DetailRow>

      <DetailRow label="Status">
        <UBadge v-if="user.active" color="success" variant="subtle">Active</UBadge>
        <UBadge v-else color="neutral" variant="outline">Deactivated</UBadge>
      </DetailRow>

      <DetailRow label="Created" :value="formatDate(user.created_at)" />
    </SbList>

    <LabeledSection v-if="user.accounts.length" title="Accounts">
      <SbListItem
        v-for="account in user.accounts"
        :key="account.id"
        :to="superAdminAccountPath(account.id)"
      >
        <SbListSection avatar>
          <UIcon :name="ICON.container" class="text-muted size-5" />
        </SbListSection>
        <SbListSection>
          <SbListLabel>{{ account.name }}</SbListLabel>
        </SbListSection>
        <SbListSection side>
          <RoleBadge :role="account.role" />
        </SbListSection>
      </SbListItem>
    </LabeledSection>

    <div class="flex flex-wrap gap-2">
      <UButton
        label="Edit"
        color="neutral"
        variant="outline"
        size="sm"
        :to="editSuperAdminUserPath(user.id)"
      />
      <template v-if="!is_current_user">
        <UButton
          :label="user.active ? 'Deactivate' : 'Reactivate'"
          color="neutral"
          variant="outline"
          size="sm"
          :loading="toggling"
          @click="toggleActive"
        />
        <UButton
          label="Delete"
          color="error"
          variant="outline"
          size="sm"
          @click="
            () => {
              deleteOpen = true
            }
          "
        />
      </template>
    </div>

    <DeleteConfirmModal
      :open="deleteOpen"
      :title="`Delete ${user.first_name}?`"
      :loading="deleting"
      @update:open="!$event && (deleteOpen = false)"
      @confirm="confirmDelete"
    >
      <p class="text-sm">This will permanently delete the user and all their data.</p>
    </DeleteConfirmModal>
  </div>
</template>
